
import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Image,
  Button,
  Link,
  Card,
  CardBody,
  CardHeader,
  Icon,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaHome, FaRocket, FaEye, FaLinkedin, FaTwitter, FaPhone } from 'react-icons/fa';
import { motion } from 'framer-motion';
import heroImage from '../../assets/PageHero2.webp';
import team1 from '../../assets/team1.jpeg';
import team2 from '../../assets/team2.jpeg';
import team3 from '../../assets/team3.jpeg';
import team4 from '../../assets/team4.jpeg';

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const teamMembers = [
  { name: 'Akhil', role: 'Founder & CEO', image: team1, linkedin: '#', twitter: '#' },
  { name: 'Jane Smith', role: 'Lead Developer', image: team2, linkedin: '#', twitter: '#' },
  { name: 'Alex Brown', role: 'Marketing Director', image: team3, linkedin: '#', twitter: '#' },
  { name: 'Emily Davis', role: 'UX Designer', image: team4, linkedin: '#', twitter: '#' },
];

const testimonials = [
  {
    quote: 'Dremotech transformed our online presence with a stunning website!',
    author: 'Sarah Johnson, TechCorp',
  },
  {
    quote: 'Their team’s expertise in AR/VR development is unmatched.',
    author: 'Michael Lee, InnovateVR',
  },
  {
    quote: 'The SEO strategies boosted our traffic significantly.',
    author: 'Laura Adams, GrowEasy',
  },
];

function AboutUsMain() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box as="section" bg="customBlue.50" minH="100vh">
      {/* Our Story */}
      <Box py={{ base: 8, md: 12 }} data-aos="fade-up">
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={8}>
            <GridItem>
              <VStack align="start" spacing={4}>
                <Heading
                  as="h2"
                  fontSize={{ base: '2xl', md: '3xl' }}
                  fontFamily="Pacifico, cursive"
                  color="customBlue.600"
                >
                  Our Story
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
                  Founded in 2020, Dremotech Solutions is a passionate team dedicated to delivering innovative digital solutions. From web development to AR/VR experiences, we empower businesses to thrive in the digital age with creativity and cutting-edge technology.
                </Text>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
                  Our journey began with a simple mission: to bridge the gap between ideas and reality. Today, we’re proud to have served over 200 clients worldwide, transforming their visions into impactful digital products.
                </Text>
              </VStack>
            </GridItem>
            <GridItem>
              <Image
                src={heroImage}
                alt="Our Story"
                borderRadius="md"
                objectFit="cover"
                w="100%"
                h={{ base: '300px', md: '400px' }}
                data-aos="fade-left"
              />
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* Mission & Vision */}
      <Box
        py={{ base: 8, md: 12 }}
        bgGradient="linear(to-r, customBlue.100, customBlue.200)"
        data-aos="fade-up"
      >
        <Container maxW="container.xl">
          <Heading
            as="h2"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontFamily="Pacifico, cursive"
            bgGradient="linear(to-r, customBlue.500, customBlue.600)"
            bgClip="text"
            textAlign="center"
            mb={10}
          >
            Mission & Vision
          </Heading>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={8}>
            <MotionBox
              initial={{ rotate: -2 }}
              whileHover={{ scale: 1.05, rotate: 0, boxShadow: 'lg' }}
              transition={{ duration: 0.3 }}
              data-aos="zoom-in"
              data-aos-delay="100"
            >
              <Card
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                border="1px solid"
                borderColor="customBlue.200"
              >
                <CardHeader display="flex" alignItems="center" gap={3}>
                  <Icon as={FaRocket} color="customBlue.500" boxSize={6} />
                  <Heading as="h3" fontSize="xl" color="customBlue.500">
                    Our Mission
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Text fontSize="md" color="gray.600">
                    To empower businesses with innovative, user-centric digital solutions that drive growth and create lasting impact.
                  </Text>
                </CardBody>
              </Card>
            </MotionBox>
            <MotionBox
              initial={{ rotate: 2 }}
              whileHover={{ scale: 1.05, rotate: 0, boxShadow: 'lg' }}
              transition={{ duration: 0.3 }}
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <Card
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                border="1px solid"
                borderColor="customBlue.200"
              >
                <CardHeader display="flex" alignItems="center" gap={3}>
                  <Icon as={FaEye} color="customBlue.500" boxSize={6} />
                  <Heading as="h3" fontSize="xl" color="customBlue.500">
                    Our Vision
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Text fontSize="md" color="gray.600">
                    To be a global leader in digital innovation, transforming ideas into reality through creativity and technology.
                  </Text>
                </CardBody>
              </Card>
            </MotionBox>
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      {/* <Box py={{ base: 8, md: 12 }} data-aos="fade-up">
        <Container maxW="container.xl">
          <Heading
            as="h2"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontFamily="Pacifico, cursive"
            color="customBlue.600"
            textAlign="center"
            mb={8}
          >
            Meet Our Team
          </Heading>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6}>
            {teamMembers.map((member, index) => (
              <MotionBox
                key={member.name}
                bg="white"
                borderRadius="md"
                boxShadow="md"
                overflow="hidden"
                whileHover={{ scale: 1.05, boxShadow: 'lg' }}
                transition={{ duration: 0.3 }}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <Image
                  src={member.image}
                  alt={member.name}
                  w="100%"
                  h={{ base: '200px', md: '250px' }}
                  objectFit="cover"
                />
                <VStack p={4} align="start" spacing={3}>
                  <Heading as="h3" fontSize="lg" color="customBlue.500">
                    {member.name}
                  </Heading>
                  <Text fontSize="sm" color="gray.600">
                    {member.role}
                  </Text>
                  <HStack spacing={3}>
                    <Link href={member.linkedin} isExternal>
                      <Icon
                        as={FaLinkedin}
                        boxSize={5}
                        color="customBlue.500"
                        _hover={{ color: 'customBlue.600', transform: 'scale(1.2)' }}
                        transition="all 0.2s"
                      />
                    </Link>
                    <Link href={member.twitter} isExternal>
                      <Icon
                        as={FaTwitter}
                        boxSize={5}
                        color="customBlue.500"
                        _hover={{ color: 'customBlue.600', transform: 'scale(1.2)' }}
                        transition="all 0.2s"
                      />
                    </Link>
                  </HStack>
                </VStack>
              </MotionBox>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Testimonials */}
      <Box py={{ base: 8, md: 12 }} bg="customBlue.100" data-aos="fade-up">
        <Container maxW="container.xl">
          <Heading
            as="h2"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontFamily="Pacifico, cursive"
            color="customBlue.600"
            textAlign="center"
            mb={8}
          >
            What Our Clients Say
          </Heading>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                bg="white"
                borderRadius="md"
                boxShadow="md"
                p={6}
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <Text fontSize="md" color="gray.600" mb={4}>
                  "{testimonial.quote}"
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="customBlue.500">
                  {testimonial.author}
                </Text>
              </Card>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
    </Box>
  );
}

export default AboutUsMain;
